import React, {useState} from "react"
import {Button, Form, Input, Select, notification, Modal, Checkbox, DatePicker, Empty, Popconfirm} from "antd"
import {Users, Plus, Heart, Baby, UserRound} from "lucide-react"
import {t} from "@lingui/core/macro"
import {Trans} from "@lingui/react/macro"

import {AccountAPI} from "./api"
import {phoneRules} from "./phoneRule"
import PhoneInput from "../WebEditor/components/PhoneInput"

function relationLabel(rel) {
    if (rel === "CONJOINT") return t`Conjoint`
    if (rel === "ENFANT") return t`Enfant`
    return rel
}

function StatePill({state}) {
    if (state === "VALIDE") return <Pill color="#1E9E6A" bg="#E6F4EC"><Trans>Validé</Trans></Pill>
    if (state === "REJECTED") return <Pill color="#C0392B" bg="#FBE9E7"><Trans>Refusé</Trans></Pill>
    return <Pill color="#C9781E" bg="#FCEFD9"><Trans>En attente</Trans></Pill>
}

function Pill({children, color, bg}) {
    return <span style={{display: "inline-block", fontSize: 12, fontWeight: 600, color, background: bg, borderRadius: 999, padding: "3px 10px"}}>{children}</span>
}

export default function Family({org, account, members, lang, primary = "#2B70A9", onRefresh}) {
    const [modalOpen, setModalOpen] = useState(false)
    const [relation, setRelation] = useState("ENFANT")
    const [noContactMeans, setNoContactMeans] = useState(false)
    const [saving, setSaving] = useState(false)
    const [form] = Form.useForm()

    const allowFamily = account?.state === "VALIDE"

    function openAdd() {
        form.resetFields()
        setRelation("ENFANT")
        setNoContactMeans(false)
        setModalOpen(true)
    }

    async function onAdd(values) {
        setSaving(true)
        const hasContactMeans = relation === "CONJOINT" ? true : !noContactMeans
        const payload = {
            relation, hasContactMeans,
            profile: {
                nom: values.nom, prenom: values.prenom,
                email: hasContactMeans ? values.email : null,
                tel: hasContactMeans ? values.tel : null,
                ddn: !hasContactMeans && values.ddn ? values.ddn.format("YYYY-MM-DD") : null,
            },
        }
        try {
            const res = await AccountAPI.addMember(payload)
            if (res?.success) {
                notification.success({message: t`Membre ajouté`})
                setModalOpen(false)
                onRefresh && onRefresh()
            } else {
                notification.error({message: mapError(res?.error)})
            }
        } finally {
            setSaving(false)
        }
    }

    function mapError(code) {
        switch (code) {
            case "SPOUSE_ALREADY_EXISTS": return t`Un conjoint est déjà enregistré. Retirez-le d'abord.`
            case "FAMILY_DISABLED": return t`La gestion de la famille n'est pas activée.`
            case "ACCOUNT_NOT_VALIDE": return t`Votre compte doit être validé pour ajouter un membre.`
            default: return t`Une erreur est survenue.`
        }
    }

    async function onRemove(id) {
        const res = await AccountAPI.removeMember(id)
        if (res?.success) {
            notification.success({message: t`Membre retiré`})
            onRefresh && onRefresh()
        } else {
            notification.error({message: t`Erreur`})
        }
    }

    return (
        <div>
            <div className="acs-panel" style={{background: "#fff", border: "1px solid #f0ece3", borderRadius: 16, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.04)"}}>
                <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 16, flexWrap: "wrap"}}>
                    <div style={{display: "flex", alignItems: "center", gap: 10}}>
                        <div style={{width: 36, height: 36, borderRadius: 10, background: primary + "1A", color: primary, display: "inline-flex", alignItems: "center", justifyContent: "center"}}><Users size={20}/></div>
                        <div style={{fontWeight: 700, fontSize: 16}}><Trans>Ma famille</Trans></div>
                    </div>
                    {allowFamily && (
                        <Button type="primary" icon={<Plus size={16}/>} onClick={openAdd} style={{borderRadius: 10, fontWeight: 600}}>
                            <Trans>Ajouter un membre</Trans>
                        </Button>
                    )}
                </div>

                {!allowFamily && (
                    <Empty description={t`Votre compte doit être validé pour gérer votre famille.`} style={{padding: "16px 0"}}/>
                )}

                {allowFamily && (!members || members.length === 0) && (
                    <Empty description={t`Aucun membre pour le moment`} style={{padding: "24px 0"}}/>
                )}

                {allowFamily && members && members.map((m) => {
                    const isSpouse = m.relation === "CONJOINT"
                    const accent = isSpouse ? "#E0457B" : "#E08D2F"
                    return (
                        <div key={m.id} style={{
                            display: "flex", alignItems: "center", gap: 12,
                            padding: "12px 14px", marginBottom: 8,
                            background: "#faf8f4", border: "1px solid #f0ece3", borderRadius: 12,
                        }}>
                            <div style={{flexShrink: 0, width: 38, height: 38, borderRadius: 10, background: accent + "16", color: accent, display: "inline-flex", alignItems: "center", justifyContent: "center"}}>
                                {isSpouse ? <Heart size={18}/> : (m.hasContactMeans === false ? <Baby size={18}/> : <UserRound size={18}/>)}
                            </div>
                            <div style={{minWidth: 0, flex: 1}}>
                                <div style={{fontWeight: 600, fontSize: 14}}>
                                    {[m.profile?.prenom, m.profile?.nom].filter(Boolean).join(" ") || t`(sans nom)`}
                                </div>
                                <div style={{display: "flex", gap: 6, alignItems: "center", marginTop: 4, flexWrap: "wrap"}}>
                                    <span style={{fontSize: 12, color: "#8a8278"}}>{relationLabel(m.relation)}</span>
                                    <StatePill state={m.state}/>
                                </div>
                            </div>
                            <Popconfirm
                                title={t`Retirer ce membre de votre compte ?`}
                                onConfirm={() => onRemove(m.id)}
                                okText={t`Retirer`} cancelText={t`Annuler`}
                            >
                                <Button danger type="text" size="small"><Trans>Retirer</Trans></Button>
                            </Popconfirm>
                        </div>
                    )
                })}
            </div>

            <Modal title={t`Ajouter un membre`} open={modalOpen} onCancel={() => setModalOpen(false)} footer={null} destroyOnHidden>
                <Form form={form} layout="vertical" onFinish={onAdd}>
                    <Form.Item label={t`Lien de parenté`}>
                        <Select
                            value={relation}
                            onChange={(v) => { setRelation(v); if (v === "CONJOINT") setNoContactMeans(false) }}
                            options={[{value: "ENFANT", label: t`Enfant`}, {value: "CONJOINT", label: t`Conjoint`}]}
                        />
                    </Form.Item>
                    <div style={{display: "flex", gap: 12, flexWrap: "wrap"}}>
                        <Form.Item name="prenom" label={t`Prénom`} rules={[{required: true}]} style={{flex: 1, minWidth: 150}}>
                            <Input/>
                        </Form.Item>
                        <Form.Item name="nom" label={t`Nom`} rules={[{required: true}]} style={{flex: 1, minWidth: 150}}>
                            <Input/>
                        </Form.Item>
                    </div>

                    {relation === "ENFANT" && (
                        <Form.Item>
                            <Checkbox checked={noContactMeans} onChange={(e) => setNoContactMeans(e.target.checked)}>
                                <Trans>Ce contact n'a pas de téléphone ni d'email</Trans>
                            </Checkbox>
                        </Form.Item>
                    )}

                    {!noContactMeans ? (
                        <>
                            <Form.Item name="email" label={t`Email`} rules={[{type: "email", message: t`Email invalide`}]}>
                                <Input/>
                            </Form.Item>
                            <Form.Item name="tel" label={t`Téléphone`} rules={phoneRules(false)}>
                                <PhoneInput defaultLang={lang}/>
                            </Form.Item>
                        </>
                    ) : (
                        <Form.Item name="ddn" label={t`Date de naissance`} rules={[{required: true, message: t`Indiquez la date de naissance`}]}>
                            <DatePicker style={{width: "100%"}} format="DD/MM/YYYY"/>
                        </Form.Item>
                    )}

                    <Button type="primary" htmlType="submit" block loading={saving} style={{borderRadius: 10, fontWeight: 600}}>
                        <Trans>Ajouter</Trans>
                    </Button>
                </Form>
            </Modal>
        </div>
    )
}
